import React, { useState, useEffect } from "react"
import { get, post, put, remove } from "../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../common/pagination"
import SearchInputWrapper from "../common/searchInputWrapper"
import {
  validateFaxNumberOnlyDigit,
  formatFaxNumber,
  prepareFinalFaxNumber,
} from "../../utils/common"
import { SquarePen, Trash2, FileSpreadsheet, TriangleAlert } from "lucide-react"

const QueueToFaxNumberAssociation = ({ reloadQueues }) => {
  const [queues, setQueues] = useState([])
  const [showPopup, setShowPopup] = useState(false)
  const [queueToEdit, setQueueToEdit] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [queueIdToDelete, setQueueIdToDelete] = useState(null)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")
  const [activeRowId, setActiveRowId] = useState(null)

  // Search Filter
  const filteredQueues = queues?.filter((queue) =>
    Object.entries(queue).some(([key, value]) => {
      if (key === "faxNumber") {
        const newSearchText = searchText.replace(/[+-]/g, "").trim()
        const newValue = value.replace(/[+-]/g, "").trim()
        return newValue
          ?.toString()
          .toLowerCase()
          .includes(newSearchText.toLowerCase())
      }
      if (key === "queueName") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase())
      }
      return false
    }),
  )

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentQueues = filteredQueues.slice(startIdx, startIdx + rowsPerPage)
  const totalPages = Math.ceil(filteredQueues.length / rowsPerPage)

  // const currentFaxes = sortedFaxes.slice(startIdx, startIdx + rowsPerPage)
  // const totalPages = Math.ceil(filteredFaxes.length / rowsPerPage)

  useEffect(() => {
    loadQueue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchQueues = async () => {
    try {
      const data = await get("/admin/queue-fax-number-associations")
      setQueues(data)
    } catch (error) {
      console.error("Failed to fetch queues", error)
    }
  }

  const loadQueue = async () => {
    setLoading(true)
    await fetchQueues()
    setLoading(false)
  }

  const handleAddQueue = async (data) => {
    setActionLoading(true)

    const isEdit = !!data?.associationId

    try {
      if (isEdit) {
        await put(`/admin/queue-fax-number-association`, {
          associationId: data.associationId,
          queueName: data.queueName,
          faxNumber: data.faxNumber,
          // queueOldName: data.queueOldName,
          // queueNewName: data.queueNewName,
        })
      } else {
        await post("/admin/queue-fax-number-association", {
          queueName: data.queueName,
          faxNumber: data.faxNumber,
        })
      }

      fetchQueues()
      reloadQueues() // Reload Left panel queues
      setShowPopup(false)
      setQueueToEdit(null)
      toast.success(`Queue ${isEdit ? "updated" : "added"} successfully`)
    } catch (error) {
      console.error(`Failed to ${isEdit ? "update" : "add"} queue`, error)
      if (
        error ===
        "The Fax Number does not exist or does not belong to the customer."
      ) {
        toast.error(
          "The fax number provided is not associated with the specified Custom Fax Manager Application ID.",
        )
      } else {
        toast.error(error)
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteQueue = async () => {
    setActionLoading(true)
    try {
      await remove(
        `/admin/queue-fax-number-association/${queueIdToDelete.associationId}`,
      )
      fetchQueues()
      reloadQueues() // Reload Left panel queues
      setQueueIdToDelete(null)
      toast.success("Queue removed successfully")
    } catch (error) {
      console.error("Failed to delete queue", error)
      toast.error("Something went wrong")
    } finally {
      setActionLoading(false)
    }
  }

  const handleExportCsv = () => {
    const headers = ["Queue Name", "Fax Number"]
    // Build rows
    const rows = filteredQueues.map((queue) => [
      queue.queueName,
      formatFaxNumber(queue.faxNumber),
    ])
    // Assemble CSV string
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (cell) =>
              // escape quotes
              `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(","),
      )
      .join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `queue_faxnumbers.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", marginTop: "20px", width: "100%" }}
        >
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div className="setting-note">
              <div>
                {/* <input
                  type="text"
                  placeholder={`Search Queues`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                /> */}
                <SearchInputWrapper
                  value={searchText}
                  placeholder="Search Queues"
                  onChange={(val) => {
                    setSearchText(val)
                    setCurrentPage(1)
                  }}
                  onClear={() => {
                    setSearchText("")
                    setCurrentPage(1)
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex" }}>
              <button
                className="faxes-button"
                onClick={() => handleExportCsv()}
                style={{ height: "28px" }}
              >
                <span>
                  {" "}
                  <FileSpreadsheet size={16} color="white" />{" "}
                </span>
                <span>Export</span>
              </button>

              <button
                className="faxes-button"
                onClick={() => setShowPopup(true)}
                style={{ marginBottom: "5px" }}
              >
                + Add Queue
              </button>
            </div>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th">Queue Name</th>

                  <th className="faxes-th">Fax Number</th>

                  {/* <th className="faxes-th">Secret</th> */}
                  <th
                    className="faxes-th"
                    style={{ textAlign: "center", width: "100px" }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentQueues.length > 0 ? (
                  currentQueues.map((queue) => (
                    <tr
                      key={queue.queueId}
                      className={`fax-item fax-action-row${activeRowId === queue.queueName ? " fax-action-active" : ""}`}
                      style={{ cursor: "unset" }}
                      onMouseEnter={() => {
                        if (activeRowId && activeRowId !== queue.queueName)
                          setActiveRowId(null)
                      }}
                    >
                      <td className="faxes-td">{queue.queueName}</td>
                      <td className="faxes-td">
                        {formatFaxNumber(queue.faxNumber)}
                      </td>

                      <td className="faxes-td" style={{ textAlign: "center" }}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setActiveRowId(queue.queueName)
                            setQueueToEdit(queue)
                          }}
                        >
                          <SquarePen size="18" color="black" />
                        </span>
                        <span
                          style={{ cursor: "pointer", marginLeft: "10px" }}
                          onClick={() => {
                            setActiveRowId(queue.queueName)
                            setQueueIdToDelete(queue)
                          }}
                        >
                          <Trash2 size="18" color="red" />
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredQueues.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {filteredQueues.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={filteredQueues.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {(showPopup || queueToEdit) && (
        <QueueForm
          onSave={handleAddQueue}
          onClose={() => {
            setShowPopup(false)
            setQueueToEdit(null)
          }}
          queueToEdit={queueToEdit}
          actionLoading={actionLoading}
        />
      )}

      {queueIdToDelete && (
        <DeleteConfirmationPopup
          onConfirm={handleDeleteQueue}
          onCancel={() => setQueueIdToDelete(null)}
          actionLoading={actionLoading}
          queueIdToDelete={queueIdToDelete}
        />
      )}
    </div>
  )
}

const QueueForm = ({ onClose, onSave, queueToEdit, actionLoading }) => {
  const [queueName, setQueueName] = useState("")
  const [faxNumber, setFaxNumber] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [isWarning, setIsWarning] = useState(false)

  // Load initial data if editing
  useEffect(() => {
    if (queueToEdit) {
      setQueueName(queueToEdit?.queueName)
      setFaxNumber(queueToEdit?.faxNumber)
    }
  }, [queueToEdit])

  const handleSubmit = (e, isConfirmed = false) => {
    e.preventDefault()

    if (!queueName.trim()) {
      setErrorMessage("Queue Name is required.")
      return
    }

    //const specialCharRegex = /^[A-Za-z0-9_-]+$/
    const specialCharRegex = /^[A-Za-z0-9_&-]+$/
    if (!specialCharRegex.test(queueName.trim())) {
      setErrorMessage(
        "Queue Name can contain only letters, numbers, ampersand(&), hyphens(-), and underscores(_).",
      )
      return
    }

    if (!faxNumber) {
      setErrorMessage("Fax Number is required")
      return
    } else {
      const errorMsg = validateFaxNumberOnlyDigit(faxNumber.trim())

      if (errorMsg) {
        setErrorMessage(errorMsg)
        return
      }
    }

    const params = {
      associationId: queueToEdit?.associationId || null,
      queueName,
      faxNumber: prepareFinalFaxNumber(faxNumber),
    }

    if (
      queueToEdit &&
      ["queueName", "faxNumber"].every(
        (field) => params[field] === queueToEdit[field],
      )
    ) {
      setErrorMessage("Neither Queue Name nor Fax Number has changed")
      return
    }

    if (
      !isWarning &&
      !isConfirmed &&
      queueToEdit &&
      queueToEdit?.faxNumber !== faxNumber
    ) {
      setIsWarning(true)
      return
    }

    onSave(params)
    setIsWarning(false)
  }

  const isTakeConfirmation = !(!isWarning || !queueToEdit)

  return (
    <div className="popup-overlay">
      <div
        className="popup"
        style={{ maxWidth: isTakeConfirmation ? "820px" : "450px" }}
      >
        {actionLoading ? (
          <div className="loading" style={{ padding: "20px" }}>
            In progress ...
          </div>
        ) : (
          <>
            <div className="checkbox-popup-header">
              <h3 className="popup-title">
                {queueToEdit ? `Update` : `Add`} Queue
              </h3>
              {!isWarning && (
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: 24,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              )}
            </div>
            {!isTakeConfirmation ? (
              <form
                className="popup-group"
                onSubmit={(e) => handleSubmit(e, false)}
              >
                <div className="form-group">
                  <label htmlFor="queueName">
                    Queue Name: <span className="mandatory">*</span>
                  </label>
                  <input
                    type="text"
                    id="queueName"
                    value={queueName}
                    onChange={(e) => {
                      setQueueName(e.target.value.trim())
                      setErrorMessage("")
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="netId">
                    Fax Number: <span className="mandatory">*</span>
                  </label>
                  <input
                    type="text"
                    id="faxNumber"
                    value={faxNumber}
                    onChange={(e) => {
                      setFaxNumber(e.target.value)
                      setErrorMessage("")
                    }}
                  />
                </div>

                {errorMessage && (
                  <div className="error-message">{errorMessage}</div>
                )}

                <div className="popup-buttons">
                  <button
                    className="popup-button cancel"
                    type="button"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button className="popup-button save" type="submit">
                    {actionLoading
                      ? `In progress ...`
                      : queueToEdit
                        ? "Update"
                        : "Create"}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <div style={{ padding: "10px" }}>
                  <p>
                    Are you sure you want to update the fax number for the
                    <strong> {queueToEdit?.queueName}</strong>?
                  </p>
                  <p>
                    Updating the fax number will permanently delete all existing
                    fax records from the Queue and NetId subfolders.
                  </p>
                  <p>
                    <TriangleAlert size={18} fill="yellow" stroke="black" style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    This operation is irreversible and deleted data cannot be
                    recovered.
                  </p>
                </div>
                <div className="popup-buttons" style={{ padding: "10px" }}>
                  <button
                    type="button"
                    className="popup-button cancel"
                    onClick={() => setIsWarning(false)}
                  >
                    No
                  </button>
                  <button
                    type="submit"
                    className="popup-button save"
                    onClick={(e) => handleSubmit(e, true)}
                  >
                    Yes, Update
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const DeleteConfirmationPopup = ({
  onConfirm,
  onCancel,
  actionLoading,
  queueIdToDelete,
}) => (
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px", maxWidth: "700px" }}>
      {actionLoading ? (
        <div>Processing...</div>
      ) : (
        <>
          {/* <h3>Confirm Deletion</h3> */}
          <p>
            Are you sure you want to delete the queue{" "}
            <strong>{queueIdToDelete?.queueName}</strong>?
          </p>
          <p>
            This action will permanently delete all associated subfolders from
            the Queue and copied fax records from NETID subfolders.
          </p>
          <p>
            <TriangleAlert size={18} fill="yellow" stroke="black" style={{ marginRight: "5px", verticalAlign: "middle" }} />
            This operation is irreversible and data cannot be recovered once
            deleted.
          </p>
          <div className="popup-buttons">
            <button className="cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)

export default QueueToFaxNumberAssociation
